/* eslint-disable */
import React, { useState, useMemo, useEffect } from 'react';
import { CheckPicker, Button, CheckboxGroup, Checkbox, InputNumber } from 'rsuite';
import { useMultiplePo } from './contexts/MultiplePoContext';
import { BaseModal } from '../BaseModal';
import { getHigherUnitAndQuantity, getHigherUnitName } from '@/Helpers';
import PoTableMultiple from '../../POTableMultiple/poTable';
import { RefreshCw, ExternalLink, Trash2, Check, X } from 'lucide-react';
import { urls } from '@/Constants';
import { SelectPicker } from 'rsuite';
import moment from 'moment';
import { getLCM, isNotMultipleOf } from './utils/utils';

const AddProductModal = ({ isOpen, onClose, onAdd }) => {
    const { getProductData, batches, tableData, refreshIngredients, state, workerAllocations } = useMultiplePo();
    const [step, setStep] = useState(1);
    const [selectedVariantIds, setSelectedVariantIds] = useState([]);
    const [variantqtys, setVariantqtys] = useState({});
    const [frequencies, setFrequencies] = useState({});
    const [productSelections, setProductSelections] = useState({});
    const [repeatDays, setRepeatDays] = useState({});

    const dropDownProducts = useMemo(() => {
        const existingKeys = tableData?.rows?.map(row => {
            const packPart = row.packSize?.packId && row.packSize.packId !== 'unpacked' ? `_${row.packSize.packId}` : '';
            return `${row._productId}_${row._variantId}${packPart}`;
        });

        const data = [];
        getProductData?.forEach(product => {
            if (!['PRODUCT', 'UNPACKED_PRODUCT'].includes(product?.skuType?.toUpperCase())) return;

            const isCountUnit = product?.unit?.toLowerCase() === 'count';
            const hasPacks = product?.packSizeConfig?.length > 0;
            const baseKey = `${product.productId}_${product.variantId}`;

            if (!isCountUnit) {
                // For non-count units, just add the product once if it doesn't exist
                if (!existingKeys?.includes(baseKey)) {
                    data.push({
                        ...product,
                        value: baseKey,
                        packId: null,
                        packSize: null,
                        uniqueKey: baseKey
                    });
                }
                return;
            }

            // For 'count' unit products
            const addUnpacked = () => {
                if (!existingKeys?.includes(baseKey)) {
                    const showUnpackedSuffix = hasPacks;
                    data.push({
                        ...product,
                        label: !showUnpackedSuffix ? `${product.label} - Unpacked` : product.label,
                        value: baseKey,
                        packId: 'unpacked',
                        packSize: { quantity: 1, boxName: 'Unpacked', packId: 'unpacked' },
                        uniqueKey: baseKey
                    });
                }
            };

            const addPacks = () => {
                const packs = product?.packSizeConfig || [];
                packs.forEach(pack => {
                    const uniqueKey = `${product.productId}_${product.variantId}_${pack.packId}`;
                    if (!existingKeys?.includes(uniqueKey)) {
                        data.push({
                            ...product,
                            label: `${product.label} - Pack of ${pack.quantity}`,
                            value: uniqueKey,
                            packId: pack.packId,
                            packSize: pack,
                            uniqueKey
                        });
                    }
                });
            };

            if (hasPacks) {
                addPacks();
            } else {
                addUnpacked();
            }
        });
        return data;
    }, [getProductData, tableData?.rows]);


    useEffect(() => {
        refreshIngredients(state?.kitchen, true)
    }, []);

    // Initialize qtys when selection changes
    useEffect(() => {
        const newqtys = { ...variantqtys };
        selectedVariantIds.forEach(id => {
            if (newqtys[id] === undefined) {
                newqtys[id] = '';
            }
        });
        // Cleanup unselected
        Object.keys(newqtys).forEach(id => {
            if (!selectedVariantIds.includes(id)) {
                delete newqtys[id];
            }
        });
        setVariantqtys(newqtys);
    }, [selectedVariantIds]);

    const selectedProducts = useMemo(() => {
        return selectedVariantIds.map(key => dropDownProducts.find(p => p.uniqueKey === key)).filter(Boolean);
    }, [selectedVariantIds, dropDownProducts]);

    const step1TableData = useMemo(() => {
        const headers = [
            { name: 'S.No', id: 'sno', width: 50, fixed: true },
            { name: 'Item Name', id: 'itemName', width: 220, fixed: true },
            { name: 'SKU Code', id: 'skuCode', width: 120 },
            { name: 'Pack Size', id: 'packSizeColumn', width: 120 },
            { name: 'Batch Size', id: 'batchSize', width: 120 },
            { name: 'Total Qty', id: 'qty', width: 120 },
            { name: 'Actions', id: 'actions', width: 60 }
        ];

        const rows = selectedProducts.map((product, index) => {
            const batchInfo = batches?.find(b => b.variantId === product.variantId);
            const batchVal = batchInfo?.batchSize || 0;
            const qty = variantqtys[product.uniqueKey];

            const [displayBatch] = getHigherUnitAndQuantity(product.unit, batchVal, false);

            // Find suggested qty if it exists in the product data
            const suggestedQty = product?.suggestedQty || product?.targetQty || 0;

            const qtyVal = parseFloat(qty) || 0;
            // Pack size is ignored: validity is batch-multiple only.
            const packSizeVal = 0;
            const isProduct = product.skuType === 'PRODUCT';

            const isInvalidBatchMultiple = isNotMultipleOf(qtyVal, batchVal);
            const isInvalidPackSizeMultiple = isProduct && isNotMultipleOf(qtyVal, packSizeVal);
            const isInvalid = isInvalidBatchMultiple || isInvalidPackSizeMultiple;

            return {
                ...product,
                sno: index + 1,
                itemName: product.viewOnlyLabel,
                skuCode: product.clientVariantId || '-',
                batchSize: displayBatch,
                qty: qty,
                _batchRaw: batchVal,
                _packRaw: product.packSize?.quantity || 1,
                hasQtyError: isInvalid
            };
        });

        return { headers, rows };
    }, [selectedProducts, batches, variantqtys]);

    const renderStep1Cell = (rowData, rowIndex, header) => {
        if (header.id === 'sno') {
            return <div className="text-center text-[12px] h-full flex items-center justify-start">{rowData.sno}</div>;
        }
        if (header.id === 'itemName') {
            return (
                <div className="text-[14px] w-full text-left text-gray-900 leading-tight h-full flex items-center px-3">
                    {rowData.viewOnlyLabel}
                </div>
            );
        }
        if (header.id === 'skuCode') {
            return <div className="text-gray-500 text-center h-full flex items-center justify-start">{rowData.skuCode}</div>;
        }
        if (header.id === 'packSizeColumn') {
            if (rowData.skuType !== 'PRODUCT' || !rowData.packId) {
                return <div className="text-gray-400 text-[12px] h-full flex items-center justify-start">-</div>;
            }
            return (
                <div className="flex flex-col items-center justify-center text-[12px] h-full w-full">
                    <span className={`px-2 py-0.5 rounded text-[11px] font-bold ${rowData.packId === 'unpacked' ? 'bg-gray-100 text-gray-600' : 'bg-blue-50 text-blue-600'}`}>
                        {rowData.packId === 'unpacked' ? 'Unpacked' : `Pack of ${rowData.packSize?.quantity}`}
                    </span>
                    {rowData.packSize?.boxName && rowData.packId !== 'unpacked' && (
                        <span className="text-[10px] text-gray-400">{rowData.packSize.boxName}</span>
                    )}
                </div>
            );
        }
        if (header.id === 'batchSize') {
            if (!rowData._batchRaw) {
                return (
                    <div className="flex items-center justify-center">
                        <Button
                            appearance="link"
                            size="xs"
                            className="text-red-500 p-0 flex items-center gap-1 text-[11px]"
                            onClick={() => window.open(`${urls.EXTENSION}${urls.VARIANT_BATCH_SIZE}?productId=${encodeURIComponent(rowData.productId)}&variantId=${encodeURIComponent(rowData.variantId)}&storeId=${encodeURIComponent(state?.kitchen)}&productName=${encodeURIComponent(rowData.itemName)}`)}
                        >
                            Add Batch Size <ExternalLink size={12} />
                        </Button>
                    </div>
                );
            }
            return <div className="text-center font-medium h-full flex items-center justify-center">{rowData.batchSize} {getHigherUnitName(rowData.unit)}</div>;
        }

        if (header.id === 'qty') {
            const qtyVal = parseFloat(variantqtys[rowData.uniqueKey]) || 0;
            const batchVal = parseFloat(rowData.batchSize) || 0;
            // Pack size is ignored: validity is batch-multiple only.
            const packSizeVal = 0;
            const isProduct = rowData.skuType === 'PRODUCT';

            // Dual-constraint validation logic
            const isInvalidBatchMultiple = isNotMultipleOf(qtyVal, batchVal);
            const isInvalidPackSizeMultiple = isProduct && isNotMultipleOf(qtyVal, packSizeVal);

            const isBatchMissing = !rowData._batchRaw || batchVal === 0;
            const lcm = batchVal > 0 && (isProduct ? packSizeVal : 1) > 0
                ? (isProduct ? getLCM(batchVal, packSizeVal) : batchVal)
                : (batchVal || (isProduct ? packSizeVal : 0) || 1);

            const suggestedQty = (lcm && qtyVal > 0) ? Math.ceil(qtyVal / lcm) * lcm : qtyVal;
            const remainder = (isProduct && packSizeVal > 0 && qtyVal > 0) ? qtyVal % packSizeVal : 0;

            const isInvalid = isInvalidBatchMultiple || isInvalidPackSizeMultiple;

            return (
                <div className='flex flex-col items-center justify-center gap-1'>
                    <input
                        type='number'
                        className={`text-center w-[100px] border focus:border-blue-500 outline-none transition-colors duration-200 rounded px-2 py-1 text-[12px] ${isInvalid ? 'border-red-500' : 'border-gray-300'
                            } ${isBatchMissing ? 'bg-gray-100 cursor-not-allowed border-gray-200' : ''}`}
                        value={variantqtys[rowData.uniqueKey] ?? ''}
                        disabled={isBatchMissing}
                        onChange={(e) => {
                            const val = e.target.value;
                            setVariantqtys(prev => ({
                                ...prev,
                                [rowData.uniqueKey]: val
                            }));
                        }}
                        onKeyDown={(e) => ['-', '+', "'", 'e', 'E'].includes(e.key) && e.preventDefault()}
                        placeholder={isBatchMissing ? 'N/A' : 'Enter qty'}
                    />
                    {isBatchMissing ? (
                        <span className='text-red-500 text-[10px] whitespace-nowrap font-medium'>
                            Batch size not defined
                        </span>
                    ) : isInvalid ? (
                        <div className='flex flex-col gap-0.5 items-center leading-tight'>
                            {isInvalidBatchMultiple && (
                                <span className='text-red-500 text-[10px] text-center'>
                                    Multiple of Batch Size ({batchVal})
                                </span>
                            )}
                            {isInvalidPackSizeMultiple && (
                                <span className='text-red-500 text-[10px] text-center'>
                                    Multiple of Pack Size ({packSizeVal})
                                </span>
                            )}
                            <div className='flex flex-wrap justify-center gap-x-2 mt-0.5'>
                                <span className='text-blue-600 text-[10px]'>
                                    Suggested: {suggestedQty}
                                </span>
                                {remainder > 0 && (
                                    <span className='text-orange-600 text-[10px]'>
                                        Leftover: {remainder.toFixed(3)}
                                    </span>
                                )}
                            </div>
                        </div>
                    ) : null}
                </div>
            );
        }
        if (header.id === 'actions') {
            return (
                <div className="flex justify-center items-center w-full h-full">
                    <Trash2
                        size={18}
                        className="text-red-400 hover:text-red-600 cursor-pointer transition-colors"
                        onClick={() => {
                            setSelectedVariantIds(prev => prev.filter(id => id !== rowData.uniqueKey));
                        }}
                    />
                </div>
            );
        }
        return null;
    };

    const dateHeaders = useMemo(() =>
        tableData?.headers?.filter((h) => String(h.id).startsWith('date_')) || [],
        [tableData.headers]
    );

    const isDateDisabled = (dateId) => {
        const dateStr = dateId.split('_')[1];
        const isPast = moment(dateStr).isBefore(moment(), 'day');
        const isHoliday = !!workerAllocations?.[dateStr]?.isHoliday;
        return isPast || isHoliday;
    };

    const step2TableData = useMemo(() => {
        const headers = [
            { name: 'S.No', id: 'sno', width: 50, fixed: true },
            { name: 'Product Name', id: 'itemName', width: 200, fixed: true },
            { name: 'Frequency', id: 'frequency', width: 150 },
            ...dateHeaders.map(h => ({ ...h, width: 60 }))
        ];

        const productsWithQty = selectedProducts.filter(p => Number(variantqtys[p.uniqueKey]) > 0);
        const rows = productsWithQty.map((p, idx) => ({
            ...p,
            sno: idx + 1,
            itemName: p.viewOnlyLabel,
            variantId: p.variantId,
            uniqueKey: p.uniqueKey,
            increaseHeight: frequencies[p.uniqueKey] !== 'daily',
        }));

        return { headers, rows };
    }, [selectedProducts, variantqtys, dateHeaders, frequencies]);

    const handleFrequencyChange = (uniqueKey, val) => {
        setFrequencies(prev => ({ ...prev, [uniqueKey]: val }));
        if (val === 'daily') {
            const availableDays = dateHeaders
                .map(h => h.id)
                .filter(id => !isDateDisabled(id));
            setProductSelections(prev => ({ ...prev, [uniqueKey]: availableDays }));
        } else if (val === 'repeat') {
            // Initialize repeat days if not set
            if (!repeatDays[uniqueKey]) {
                setRepeatDays(prev => ({ ...prev, [uniqueKey]: 2 }));
            }
            // Clear selections, user needs to pick a starting date
            setProductSelections(prev => ({ ...prev, [uniqueKey]: [] }));
        } else if (val === 'custom') {
            // Keep existing selections
        }
    };

    const handleRepeatDaysChange = (uniqueKey, val) => {
        setRepeatDays(prev => ({ ...prev, [uniqueKey]: val }));
        // If there are already selections, recompute repeat from first selected date
        const currentSelections = productSelections[uniqueKey] || [];
        if (currentSelections.length > 0) {
            const sortedIds = dateHeaders.map(h => h.id).filter(id => currentSelections.includes(id));
            if (sortedIds.length > 0) {
                applyRepeatPattern(uniqueKey, sortedIds[0], val);
            }
        }
    };

    const applyRepeatPattern = (uniqueKey, startDateId, repeatInterval) => {
        const sortedDateIds = dateHeaders.map(h => h.id);
        const startIndex = sortedDateIds.indexOf(startDateId);
        if (startIndex === -1) return;

        const interval = (parseInt(repeatInterval) || 2) + 1;
        const selected = [];

        // Go backward from start
        for (let i = startIndex; i >= 0; i -= interval) {
            const dateId = sortedDateIds[i];
            if (!isDateDisabled(dateId)) selected.push(dateId);
        }
        // Go forward from start
        for (let i = startIndex + interval; i < sortedDateIds.length; i += interval) {
            const dateId = sortedDateIds[i];
            if (!isDateDisabled(dateId)) selected.push(dateId);
        }
        setProductSelections(prev => ({ ...prev, [uniqueKey]: selected }));
    };

    const toggleDaySelection = (uniqueKey, dateId) => {
        if (isDateDisabled(dateId)) return;
        const freq = frequencies[uniqueKey] || 'custom';

        if (freq === 'daily') return; // Daily mode: can't toggle individual days

        if (freq === 'repeat') {
            // In repeat mode, clicking a date sets it as the starting date
            const interval = repeatDays[uniqueKey] || 2;
            applyRepeatPattern(uniqueKey, dateId, interval);
            return;
        }

        // Custom mode
        setProductSelections(prev => {
            const current = prev[uniqueKey] || [];
            if (current.includes(dateId)) {
                return { ...prev, [uniqueKey]: current.filter(id => id !== dateId) };
            } else {
                return { ...prev, [uniqueKey]: [...current, dateId] };
            }
        });
    };

    const handleBulkAction = (uniqueKey, action) => {
        if (action === 'all') {
            const availableDays = dateHeaders
                .map(h => h.id)
                .filter(id => !isDateDisabled(id));
            setProductSelections(prev => ({ ...prev, [uniqueKey]: availableDays }));
        } else {
            setProductSelections(prev => ({ ...prev, [uniqueKey]: [] }));
        }
        setFrequencies(prev => ({ ...prev, [uniqueKey]: 'custom' }));
    };

    const renderStep2Cell = (rowData, rowIndex, header) => {
        if (header.id === 'sno') {
            return <div className="flex w-full h-full items-center justify-center">{rowData.sno}</div>;
        }
        if (header.id === 'itemName') {
            return (
                <div className="flex w-full h-full items-center justify-start px-3 py-2">
                    <div className="flex flex-col text-left">
                        <span className="font-bold text-[14px] text-gray-900 leading-tight">{rowData.productName}</span>
                        <span className="text-[11px] text-gray-500 font-semibold mt-0.5">{rowData.viewOnlyLabel.split('(')[1]?.replace(')', '') || ''}</span>
                    </div>
                </div>
            );
        }
        if (header.id === 'frequency') {
            const freq = frequencies[rowData.uniqueKey] || 'custom';
            const hasSelectedDate = (productSelections[rowData.uniqueKey]?.length || 0) > 0;
            return (
                <div className='flex flex-col gap-1 items-center justify-center px-1 w-full'>
                    {freq === 'repeat' && !hasSelectedDate && (
                        <div
                            className='text-blue-500 text-[10px] font-semibold text-center'
                            style={{ animation: 'pulse 4s cubic-bezier(0.4, 0, 0.6, 1) infinite' }}
                        >
                            Select a starting<br />date to repeat
                        </div>
                    )}
                    <SelectPicker
                        data={[
                            { label: 'Daily', value: 'daily' },
                            { label: 'Repeat After', value: 'repeat' },
                            { label: 'Custom', value: 'custom' },
                        ]}
                        searchable={false}
                        cleanable={false}
                        size="xs"
                        value={freq}
                        onChange={(val) => handleFrequencyChange(rowData.uniqueKey, val)}
                        className="w-full min-w-[100px]"
                        menuClassName='z-[999999]'
                    />
                    {freq === 'repeat' && (
                        <input
                            type='number'
                            className='w-full text-center border border-gray-300 rounded px-1 py-0.5 text-[11px] focus:border-blue-500 outline-none transition-colors'
                            value={repeatDays[rowData.uniqueKey] ?? 2}
                            onChange={(e) => handleRepeatDaysChange(rowData.uniqueKey, e.target.value)}
                            onKeyDown={(e) => ['-', '+', "'", 'e', 'E'].includes(e.key) && e.preventDefault()}
                            min={1}
                            placeholder='Interval'
                        />
                    )}
                    {freq === 'custom' && (
                        <div className='flex items-center gap-1'>
                            <button
                                onClick={() => handleBulkAction(rowData.uniqueKey, 'all')}
                                className='px-2 py-0.5 rounded border border-blue-200 bg-blue-50 text-blue-600 text-[10px] font-bold hover:bg-blue-100 transition-colors shadow-sm'
                            >
                                Select All
                            </button>
                            <button
                                onClick={() => handleBulkAction(rowData.uniqueKey, 'none')}
                                className='px-2 py-0.5 rounded border border-blue-200 bg-blue-50 text-blue-600 text-[10px] font-bold hover:bg-blue-100 transition-colors shadow-sm'
                            >
                                DeSelect All
                            </button>
                        </div>
                    )}
                </div>
            );
        }
        if (String(header.id).startsWith('date_')) {
            const isDisabled = isDateDisabled(header.id);
            const isSelected = productSelections[rowData.uniqueKey]?.includes(header.id);

            return (
                <div className="flex w-full h-full items-center justify-center">
                    <div
                        className={`w-5 h-5 flex items-center justify-center rounded border transition-colors duration-200 ${isDisabled
                            ? 'bg-gray-100 border-gray-300 cursor-not-allowed'
                            : isSelected
                                ? 'bg-green-600 border-green-600 shadow-sm cursor-pointer text-white'
                                : 'bg-white border-gray-800 hover:border-blue-400 cursor-pointer'
                            }`}
                        onClick={() => !isDisabled && toggleDaySelection(rowData.uniqueKey, header.id)}
                    >
                        {isDisabled ? (
                            <X size={14} className='text-gray-400' strokeWidth={3} />
                        ) : isSelected && (
                            <Check size={16} strokeWidth={3} />
                        )}
                    </div>
                </div>
            );
        }
        return null;
    };

    const handleNext = () => setStep(2);
    const handleBack = () => setStep(1);

    const isNextDisabled = useMemo(() => {
        const rows = step1TableData.rows;
        if (rows.length === 0) return true;

        const hasQty = rows.some((row) => row?.qty && row.qty !== '');
        if (!hasQty) return true;
        const isInvalid = rows.some((row) => {
            const qty = parseFloat(row.qty);
            const batch = parseFloat(row.batchSize);
            // const pack = row.skuType === 'PRODUCT' ? (parseFloat(row.packSize?.quantity) || 1) : 1;
            const pack = 1;
            const constraint = (batch > 0 && pack > 0) ? getLCM(batch, pack) : (batch || pack || 1);
            return isNotMultipleOf(qty, constraint);
        });
        return isInvalid;
    }, [step1TableData]);

    const handleAdd = () => {
        const productsToAdd = selectedProducts.map(p => {
            const qty = Number(variantqtys[p.uniqueKey]);
            const selectedDaysForProduct = productSelections[p.uniqueKey] || [];
            const batchInfo = batches?.find(b => b.variantId === p.variantId);
            const batchRaw = batchInfo?.batchSize || 0;
            // const packQty = p.skuType === 'PRODUCT' ? (p.packSize?.quantity || 1) : 1;
            const packQty = 1;
            const constraint = (batchRaw > 0 && packQty > 0) ? getLCM(batchRaw, packQty) : (batchRaw || packQty || 1);

            const [displayBatch] = getHigherUnitAndQuantity(p.unit, batchRaw, false);

            return {
                product: p,
                quantity: qty,
                batch: displayBatch,
                selectedDays: selectedDaysForProduct,
                packSize: p.packSize,
                packId: p.packId,
                _isUnpacked: p.packId === 'unpacked'
            };
        }).filter(p => p.quantity > 0 && p.selectedDays.length > 0);
        if (productsToAdd.length === 0) return;
        onAdd(productsToAdd);

        // Reset
        setSelectedVariantIds([]);
        setVariantqtys({});
        setFrequencies({});
        setProductSelections({});
        setRepeatDays({});
        setStep(1);
    };

    const renderFooter = () => (
        <div className="flex justify-between w-full items-center p-4 border-t border-gray-100 bg-gray-50 rounded-b-lg">
            <div>
                {step === 2 && (
                    <Button onClick={handleBack} appearance="subtle" className="font-semibold">
                        Back to Selection
                    </Button>
                )}
            </div>
            {step === 1 ? (
                <Button
                    onClick={handleNext}
                    appearance="primary"
                    className="bg-teal-600 hover:bg-teal-700 text-white px-8"
                    disabled={isNextDisabled}
                >
                    Next: Select Days
                </Button>
            ) : (
                <Button
                    onClick={handleAdd}
                    appearance="primary"
                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-8"
                    disabled={!selectedProducts.some(p => (productSelections[p.uniqueKey]?.length || 0) > 0)}
                >
                    Add {selectedProducts.filter(p => (productSelections[p.uniqueKey]?.length || 0) > 0).length} Product(s) to Plan
                </Button>
            )}
        </div>
    );

    return (
        <BaseModal
            open={isOpen}
            handleClose={onClose}
            heading={step === 1 ? "Add Products: Select & Qty" : "Add Products: Select Days"}
            width="95vw"
            height='75vw'
            renderBtns={renderFooter}
        >
            <div className="flex flex-col gap-4 p-2 h-full overflow-hidden">
                {step === 1 ? (
                    <div className="flex flex-col gap-4 h-full overflow-hidden">
                        <div className="flex flex-col gap-2 flex-shrink-0">
                            <div className="flex justify-between items-center">
                                <label className="text-[13px] font-bold text-gray-700 uppercase tracking-wider">
                                    Select Products ({selectedVariantIds.length})
                                </label>
                                <Button
                                    size="xs"
                                    appearance="subtle"
                                    onClick={() => refreshIngredients(state?.kitchen, true)}
                                    className="flex items-center gap-1 text-teal-600 hover:text-teal-700"
                                >
                                    <RefreshCw size={14} /> Refresh Batches
                                </Button>
                            </div>
                            <CheckPicker
                                data={dropDownProducts}
                                value={selectedVariantIds}
                                onChange={setSelectedVariantIds}
                                block
                                searchable
                                placeholder="Search and select products (including pack variants)..."
                                className="w-full"
                                menuClassName='z-[999999]'
                                virtualized
                                renderValue={(value, items) => {
                                    return (
                                        <div className="flex gap-1.5 flex-wrap">
                                            {items.map(item => (
                                                <span key={item.value} className="px-2.5 py-1 bg-gray-100 border border-gray-200 rounded text-[12px] flex items-center gap-1 font-semibold shadow-sm">
                                                    {item.label}
                                                </span>
                                            ))}
                                        </div>
                                    );
                                }}
                            />
                        </div>

                        {selectedVariantIds.length > 0 && (
                            <div className="border border-gray-200 rounded-lg overflow-hidden shadow-sm flex-1">
                                <PoTableMultiple
                                    tableData={step1TableData}
                                    renderCellData={renderStep1Cell}
                                    headerHeight={50}
                                />
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="flex flex-col gap-4 h-full overflow-hidden">
                        <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-3 flex-shrink-0">
                            <p className="text-xs text-indigo-700 font-medium text-center">
                                Select production days for each product. Quantity will be distributed equally among selected days.
                                <br />
                                <span className="text-[10px] opacity-75 font-normal">Past dates and holidays are automatically disabled.</span>
                            </p>
                        </div>

                        <div className="border border-gray-200 rounded-lg shadow-sm flex-1 overflow-y-auto">
                            <PoTableMultiple
                                tableData={step2TableData}
                                renderCellData={renderStep2Cell}
                                headerHeight={60}
                            />
                        </div>
                    </div>
                )}
            </div>
        </BaseModal>
    );
};

export default AddProductModal;
